import { Button, Grid, Box, Stack, Typography, CircularProgress } from "@mui/material";
import SnackbarComponent from "./Snackbar";
import { MyLocation } from "@mui/icons-material";
import {
  FREE_MAILING_ADDRESS_REGEX,
  FREE_WORK_ADDRESS_REGEX,
  IMAGES,
  WEB_COLORS,
} from "@/lib/constants";
import Image from "next/image";
import TextFieldComponent from "./TextField";
import {  losApi, useGetMapAddressQuery, useLazyGetReverseGeocodeMapplsQuery } from "@/lib/redux";
import MapsAutocomplete from "./Maps/MapAutoComplete";
import MapsComponent from "./Maps/Maps";
import { useFormik } from "formik";
import { configureStore, createSlice } from "@reduxjs/toolkit";
import { Provider, shallowEqual, useDispatch, useSelector } from "react-redux";
import { GoogleMapApi } from "@/lib/redux/apis/google-map";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import MapplsAutosuggestion from "./MapMyIndia/MapplsAutoSuggestion";
import {
  mapplsApi,
} from "@/lib/redux/apis/map-my-india";
import MapplsMap from "./MapMyIndia/Mappls";
import InfoTile from "./InfoTile";

const createAddressSchema = (labels) => {
  // Custom test: no leading/trailing spaces, min 10 length after trim
  const min10Trimmed = [
    "min10Trimmed",
    "Min 10 characters required, no leading/trailing spaces allowed",
    (value) => {
      if (typeof value !== "string") return false;
      if (value !== value.trim()) return false;
      return value.trim().length >= 10;
    },
  ];

  const min10TrimmedLine5 = [
    "min10Trimmed",
    "Min 10 characters required, no leading/trailing spaces allowed",
    (value) => {
      if (value === "" || value === null || value === undefined) return true;
      if (typeof value !== "string") return false;
      if (value !== value.trim()) return false;
      return value.trim().length >= 10;
    },
  ];

  return Yup.object({
    line2: Yup.string()
      .required(`${labels.line2} is required`)
      .test(...min10Trimmed),
    line3: Yup.string()
      .required(`${labels.line3} is required`)
      .test(...min10Trimmed),
    line5: Yup.string()
      .notRequired()
      .nullable()
      .test(...min10TrimmedLine5),
    pin: Yup.string().required("PIN is required"),
    city: Yup.string()
      .trim()
      .required("City is required")
      .matches(/^[A-Za-z0-9\s.,'&/()-]+$/),
    state: Yup.string()
      .trim()
      .required("State is required")
      .matches(/^[A-Za-z\s.,&/'()-]+$/),
    landmark: Yup.string()
      .required(`${labels.landmark} is required`)
      .test(...min10Trimmed),
    country: Yup.string()
      .oneOf(["India"], "Address country must be India")
      .required("Country is required"),
  });
};

function getAddressError({ pin, city, state, country }) {
  const hasPin = !!pin;
  const hasCity = !!city;
  const hasState = !!state;
  const hasCountry = !!country;

  if (!hasPin && !hasState && !hasCity && !hasCountry) {
    return "Pincode/State/City/Country not found for the search item, please retry.";
  }

  if (!hasPin) {
    return "Pincode not found. Please check and re-enter the address";
  }

  if (!hasState) {
    return "State not found. Please check and re-enter the address";
  }

  if (!hasCity) {
    return "City not found. Please check and re-enter the address";
  }

  if (!hasCountry) {
    return "Country not found. Please check and re-enter the address";
  }

  return "";
}

// address redux store

export const Stages = {
  location: "location",
  map: "map",
  addressFields: "addressFields",
};
const initialState = {
  line2: "",
  line3: "",
  line5: "",
  landmark: "",
  stage: Stages.location,
  latitude: null,
  longitude: null,
  googleApiAddData: null,
  mapplsData: null,
  googleApiFallback: false,
  city: null,
  country: null,
  state: null,
  pin: null,
  errorMessage: null,
  start: Stages.location,
  googleSearched: false,
};

const StoreKeys = Object.freeze(
  Object.keys(initialState).reduce((acc, key) => {
    acc[key] = key;
    return acc;
  }, {}),
);

const storeSlice = createSlice({
  name: "addressStore",
  initialState,
  reducers: {
    setStore: (state, action) => {
      Object.assign(state, action.payload);
    },
    resetStore: (state) => ({
      ...initialState,
      start: state.start,
      stage: state.start,
    }),
  },
});

const addressStore = configureStore({
  reducer: {
    [storeSlice.name]: storeSlice.reducer,
    [GoogleMapApi.reducerPath]: GoogleMapApi.reducer,
    [mapplsApi.reducerPath]: mapplsApi.reducer,
    [losApi.reducerPath]: losApi.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware().concat(
      GoogleMapApi.middleware,
      mapplsApi.middleware,
      losApi.middleware
    );
  },
});

const useStore = (...keys) => {
  return useSelector((state) => {
    const slice = state?.addressStore || {};
    return keys.reduce((acc, key) => {
      acc[key] = slice[key];
      return acc;
    }, {});
  }, shallowEqual);
};

const useDispatchStore = () => {
  const dispatch = useDispatch();
  const dispatchStore = (payload) => {
    dispatch(storeSlice.actions.setStore(payload));
  };
  const resetStore = () => {
    dispatch(storeSlice.actions.resetStore());
  };

  return { dispatchStore, resetStore };
};

export const AddressModule = ({ children }) => {
  return <Provider store={addressStore}>{children}</Provider>;
};

const useGoogleApiHook = ({ latitude, longitude, onSuccess, skip }) => {
  const {dispatchStore} = useDispatchStore();
  const { data: googleApiAddData, refetch } = useGetMapAddressQuery(
    {
      latitude,
      longitude,
    },
    { skip: !latitude || !longitude || skip, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if(!latitude || !longitude || skip ) return;
    const mapAddressData = googleApiAddData;
    if (mapAddressData) {
      const AddressComponents =
        mapAddressData?.results?.[0]?.formatted_address || [];
      const components = mapAddressData?.results?.[0]?.address_components || [];
      const hasPincode = components?.some((address) =>
        address?.types?.includes("postal_code"),
      );
      const hasState = components?.some((address) =>
        address?.types?.includes("administrative_area_level_1"),
      );
      const hasCity = components?.some((address) =>
        address?.types?.includes("locality"),
      );
      let addressDetails = null;
      let errorMessage = "";
      if (components && (!hasPincode || !hasState || !hasCity)) {
        if (!hasPincode && !hasState && !hasCity) {
          addressDetails = {
            pin: "",
            state: "",
            city: "",
          };
          errorMessage =
            "Pincode/State/City not found for the search item, please retry.";
          // dispatchStore({ ...addressDetails, errorMessage });
          dispatchStore({
            ...addressDetails,
            errorMessage,
            stage: Stages.addressFields,
          });
          return;
        } else if (!hasPincode) {
          addressDetails = { pin: "" };
          errorMessage =
            "Pincode not found. Please check and re-enter the address";
          // dispatchStore({ ...addressDetails, errorMessage });
          dispatchStore({
            ...addressDetails,
            errorMessage,
            stage: Stages.addressFields,
          });
          return;
        } else if (!hasState) {
          addressDetails = { state: "" };
          errorMessage =
            "State not found. Please check and re-enter the address";
          // dispatchStore({ ...addressDetails, errorMessage });
          dispatchStore({
            ...addressDetails,
            errorMessage,
            stage: Stages.addressFields,
          });

          return;
        } else if (!hasCity) {
          addressDetails = { city: "" };
          errorMessage =
            "City not found. Please check and re-enter the address";
          // dispatchStore({ ...addressDetails, errorMessage });
          dispatchStore({
            ...addressDetails,
            errorMessage,
            stage: Stages.addressFields,
          });

          return;
        }
      } else {
        errorMessage = "";
      }

      const workAddress = AddressComponents?.split(",")
        .map((part) => part.trim())
        .filter((part) => part && !part.includes("+"))
        .join(", ");
      const country = mapAddressData?.results[0]?.address_components?.find(
        (address) => address?.types?.includes("country"),
      )?.long_name;

      const city = mapAddressData?.results[0]?.address_components?.find(
        (address) => address?.types?.includes("locality"),
      )?.long_name;

      const state = mapAddressData?.results[0]?.address_components?.find(
        (address) => address?.types?.includes("administrative_area_level_1"),
      )?.long_name;

      const pin =
        mapAddressData?.results[0]?.address_components?.find((address) =>
          address?.types?.includes("postal_code"),
        )?.long_name || "";

      addressDetails = {
        state,
        country,
        city,
        pin,
        completeAddress: workAddress,
      };
      dispatchStore({
        googleApiAddData,
        ...addressDetails,
        errorMessage: null,
      });
      onSuccess && onSuccess();
    }
    if (
      mapAddressData?.results[0]?.address_components &&
      (!mapAddressData?.results[0]?.address_components?.some((address) =>
        address?.types?.includes("postal_code"),
      ) ||
        !mapAddressData?.results[0]?.address_components?.some((address) =>
          address?.types?.includes("administrative_area_level_1"),
        ))
    ) {
      dispatchStore({
        pin: "",
        state: "",
      });

      SnackbarComponent({
        error: true,
        errorMessage:
          "Pincode Not found for the searched location, Please modify your search.",
      });
    }
  }, [googleApiAddData, latitude, longitude, onSuccess, skip]);

  return { refetch, data: googleApiAddData };
};

function CurrentLocation({ label = "Use Current Location", nextStage, sx = {}, onClick}) {
  const { latitude, longitude, googleApiFallback , googleSearched} = useStore(
    StoreKeys.latitude,
    StoreKeys.longitude,
    StoreKeys.googleApiFallback,
    StoreKeys.googleSearched,
  );

  const { dispatchStore } = useDispatchStore();

  const handleCurrentAddress = useCallback(() => {
    nextStage && dispatchStore({ stage: nextStage || Stages.map });
  }, [dispatchStore, nextStage]);

  const skipGoogleApi = !(googleApiFallback && googleSearched);
  const { refetch } = useGoogleApiHook({
    latitude,
    longitude,
    skip: skipGoogleApi,
    onSuccess: handleCurrentAddress,
  });

  const [
    getReverseGeocoded,
    {
      data: reverseGeocodedData,
      isLoading: reverseGeocodedLoading,
      error: reverseGeocodedError,
      reset: resetReverseGeocoded,
    },
  ] = useLazyGetReverseGeocodeMapplsQuery();

  const getMapplsAddress = useCallback(
    async (lat, lng) => {
      try {
        const res = await getReverseGeocoded({
          lat,
          lng,
        })?.unwrap();

        const address = res?.data?.reverseGeocode?.results?.[0];

        const {
          city,
          state,
          area: country,
          district,
          pincode: pin,
        } = address || {};

        const coordinates = {
          latitude: lat,
          longitude: lng,
        };

        dispatchStore({
          ...coordinates,
          city: city || district,
          state,
          country,
          pin,
          mapplsData: {
            placeAddress: address?.formatted_address,
            placeName:
              address?.subSubLocality ||
              address?.subLocality ||
              address?.street,
          },
          stage: nextStage || Stages.map,
        });
      } catch (error) {
        dispatchStore({ googleApiFallback: true });
      }
    },
    [getReverseGeocoded, dispatchStore],
  );


  const [isLoading, setIsLoading] = useState(false);
  const getCurrentLocation = useCallback(() => {
    setIsLoading(true);
    
    if (!navigator?.geolocation) {
      SnackbarComponent({
        error: true,
        errorMessage: "Geolocation is not supported by this browser.",
      });
      setIsLoading(false);
      return;
    }

    // Try high accuracy first with short timeout
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        dispatchStore({
          longitude: pos.coords.longitude,
          latitude: pos.coords.latitude,
        });
        !googleApiFallback && getMapplsAddress(pos.coords.latitude, pos.coords.longitude);
        setIsLoading(false);
      },
      (error) => {
        // If high accuracy fails, try low accuracy as fallback
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            dispatchStore({
              longitude: pos.coords.longitude,
              latitude: pos.coords.latitude,
            });
            !googleApiFallback && getMapplsAddress(pos.coords.latitude, pos.coords.longitude);
            setIsLoading(false);
          },
          () => {
            dispatchStore({ stage: Stages.location });
            SnackbarComponent({
              error: true,
              errorMessage: "Unable to fetch current location. Please check your location permissions.",
            });
            setIsLoading(false);
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 10000,
          }
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0, // Don't use cached position
      }
    );
  }, [dispatchStore]);

  return (
    <button
      style={{
        marginTop: 8,
        marginBottom: 8,
        padding: "8px 16px",
        background: "#fff",
        cursor: "pointer",
        fontWeight: 600,
        alignSelf: "flex-center",
        display: "flex",
        alignItems: "center",
        gap: 3,
        color: isLoading? WEB_COLORS.disabledColor  :WEB_COLORS.primary,
        width: "fit-content",
        border: "none",
        ...sx,
      }}
      disabled={isLoading}
      onClick={() => {
        getCurrentLocation();
        onClick?.();
      }}>
      <MyLocation color={WEB_COLORS.primary} />{" "}
      <span
        style={{
          color: isLoading? WEB_COLORS.disabledColor  :WEB_COLORS.primary,
          textDecoration: "underline",
        }}>
        {label}
      </span>
      {isLoading && <CircularProgress size={16} sx={{ml: 1}} disableShrink />}
    </button>
  );
}

function Map({ autosuggestion = true }) {
  // const [googleSearched, setGoogleSearched] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const mapplsRef = useRef();
  const googleAutoSuggestionRef = useRef();
  const {
    latitude,
    longitude,
    googleApiAddData,
    googleApiFallback,
    mapplsData,
    start,
    googleSearched,
  } = useStore(
    StoreKeys.latitude,
    StoreKeys.longitude,
    StoreKeys.googleApiAddData,
    StoreKeys.googleApiFallback,
    StoreKeys.mapplsData,
    StoreKeys.start,
    StoreKeys.googleSearched,
  );
  const { dispatchStore } = useDispatchStore();

  const skipGoogleApi = !(googleApiFallback && googleSearched);
  useGoogleApiHook({ latitude, longitude, skip: skipGoogleApi });

  const handleCoordinates = useCallback(
    ({ lat, long, googleApiAddData: apiData }) => {
      const payload = {};
      if (lat) payload.latitude = lat;
      if (long) payload.longitude = long;
      if (apiData)
        payload.googleApiAddData = {
          results: apiData,
          status: "ok",
        };
      payload.googleSearched = true;
      dispatchStore(payload);
    },
    [dispatchStore],
  );

  const handleMapAddress = useCallback((address, error) => {
    if (error) {
      console.error("Error fetching address:", error);
      if(error?.skipFallback) return;
      dispatchStore({ googleApiFallback: true });
      return;
    }
    const { lat, lng } = address || {};
    const payload = {};
    if (lat) payload.latitude = lat;
    if (lng) payload.longitude = lng;
    payload.city = address?.city || address?.district;
    payload.state = address?.state;
    payload.country = address?.area;
    payload.pin = address?.pincode;
    payload.mapplsData = {
      placeAddress: address?.placeAddress || address?.formatted_address,
      placeName:
        address?.placeName ||
        address?.subSubLocality ||
        address?.subLocality ||
        address?.street,
    };
    dispatchStore(payload);
  }, [dispatchStore]);

  // const onLoadStatusChange = useCallback((isLoaded) => {
  //   setGoogleLoaded(isLoaded);
  // }, [setGoogleLoaded]);

  const onDragStart= useCallback(() => {
    dispatchStore({ googleSearched: true });
  }, [dispatchStore]);

  return (
    <Stack position={"relative"}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          width: "100%",
          zIndex: 100,
          background: "#fff",
          p: 1,
        }}>
        <Stack
          direction={"row"}
          alignItems="center"
          justifyContent={"space-between"}
          mb={1}>
          <Typography
            fontSize={{ xs: 18, sm: 24 }}
            fontWeight={700}
            color={WEB_COLORS.primary}>
            Confirm Your Address
          </Typography>
          <button
            style={{
              padding: "6px 18px",
              border: `1px solid ${WEB_COLORS.primary}`,
              color: WEB_COLORS.primary,
              background: "#fff",
              borderRadius: 4,
              fontWeight: 600,
              cursor: "pointer",
              width: "fit-content",
            }}
            onClick={() => {
              mapplsRef.current?.resetMappls?.();
              googleAutoSuggestionRef?.current?.reset?.();

              dispatchStore({
                stage: start,
                googleApiAddData: null,
                googleSearched: false,
                latitude: null,
                longitude: null,
                mapplsData: null,
              });
            }}>
            Edit
          </button>
        </Stack>
      </Box>
      {googleApiFallback ? (
        <Box position="relative">
          <MapsComponent
            // onLoadStatusChange={onLoadStatusChange}
            onDragStart={onDragStart}
            sx={{
              maxHeight: "450px",
              borderRadius: "16px",
              border: "1px solid grey",
              margin: "8px",
            }}
            latitude={latitude}
            longitude={longitude}
            setLatitude={(latitude) => dispatchStore({ latitude })}
            setLongitude={(longitude) => dispatchStore({ longitude })}
          />
          {/* {googleLoaded && ( */}
          <div
            style={{
              position: "absolute",
              maxWidth: 500,
              zIndex: 999,
              top: 30,
              left: "50%",
              width: "80%",
              transform: "translateX(-50%)",
              marginY: 3,
            }}>
            {autosuggestion && (
              <>
                <MapsAutocomplete
                  label={"Search your Address"}
                  handleOnSelect={(res) => handleCoordinates(res)}
                  ref={googleAutoSuggestionRef}
                  sx={{
                    // width: "80%",
                    // maxWidth: 500,
                    // position: "absolute",
                    zIndex: 999,
                    fontSize: 15,
                    outline: "none",
                    bgcolor: "#fff",
                    // top: 5,
                    // left: "50%",
                    borderRadius: "8px",
                    // transform: "translateX(-50%)",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
                  }}
                />
                <Box
                  sx={{
                    width: "full",
                    background: "#fff",
                    borderRadius: 2,
                    mt: 1,
                    border: `1px solid ${WEB_COLORS.lightSkyBlue}`,
                    padding: "6px 1px",
                  }}>
                  <CurrentLocation
                    sx={{
                      background: "transparent",
                      scale: 0.8,
                      margin: 0,
                      padding: 0,
                    }}
                    onClick={() => dispatchStore({ googleSearched: true })}
                  />
                </Box>
              </>
            )}
          </div>
          {/* )} */}
        </Box>
      ) : (
        <MapplsMap
          ref={mapplsRef}
          defaultLatLng={{
            lat: latitude,
            lng: longitude,
          }}
          sx={{
            maxHeight: "450px",
            borderRadius: "16px",
            border: "1px solid grey",
            margin: "8px",
          }}
          plugins={autosuggestion ? ["search"] : []}
          onError={(error) => {
            console.log(
              "Error loading Mappls map, falling back to Google Maps",
              error,
            );
            if(error?.type === "CURRENT_LOCATION_OUT_OF_INDIA"){
              dispatchStore({ errorMessage: error?.message, stage: Stages.addressFields });
              return;
            }
            if (error?.skipFallback) return;
            dispatchStore({ googleApiFallback: true });
          }}
          coordinatesObserver={handleMapAddress}
        />
      )}
      <Stack>
        <InfoTile text="Incorrect information will lead to immediate rejection" />
        <InfoTile text="Physical verification over this address might be conducted" />
      </Stack>
      {((googleApiFallback && googleApiAddData) ||
        (!googleApiFallback && mapplsData)) && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            zIndex: 10,
            background: "#fff",
            p: 1,
          }}>
          <DisplayMapAddress />
          <button
            style={{
              padding: "12px 24px",
              background: WEB_COLORS.primary,
              color: "#fff",
              borderRadius: 4,
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
              border: "none",
              fontSize: 16,
              display: (
                googleApiFallback
                  ? !googleApiAddData
                  : !mapplsData?.placeName && !mapplsData?.placeAddress
              )
                ? "none"
                : "block",
            }}
            onClick={() => dispatchStore({ stage: Stages.addressFields })}>
            Confirm Address
          </button>
        </Box>
      )}
    </Stack>
  );
}

function DisplayMapAddress({ infoTile }) {
  const { googleApiAddData, mapplsData, googleApiFallback, errorMessage } =
    useStore(
      StoreKeys.googleApiAddData,
      StoreKeys.mapplsData,
      StoreKeys.googleApiFallback,
      StoreKeys.errorMessage,
    );
  if (errorMessage) return <></>;
  if (googleApiFallback ? !googleApiAddData : !mapplsData?.placeName && !mapplsData?.placeAddress) return <></>;
  return (
    <Stack
      direction={"row"}
      alignItems="center"
      gap={{ xs: 1, sm: 2 }}
      p={{ xs: 1, sm: 2 }}
      bgcolor={"rgba(242, 247, 247, 0.6)"}
      borderRadius={4}
      border={`1px solid rgba(229, 241, 241, 1)`}
      my={{ xs: 1, sm: 2 }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "36px", sm: "48px" },
          height: "auto",
          position: "relative",
          aspectRatio: "1/1",
        }}>
        <Image
          src={IMAGES.locationPin}
          alt="Map Pin"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
      <Stack>
      <Stack
        style={{
          padding: "4px 0",
          fontWeight: 500,
          color: "#333",
        }}>
        {!googleApiFallback ? (
          <Box>
            <Typography fontSize={{ xs: 14, sm: 20 }} fontWeight={400}>
              {mapplsData?.placeName}
            </Typography>
            <Typography fontSize={{ xs: 14, sm: 20 }} fontWeight={400}>
              {mapplsData?.placeAddress}
            </Typography>
          </Box>
        ) : (
          <Box>
            {" "}
            <Typography fontSize={{ xs: 14, sm: 20 }} fontWeight={400}>
              {
                googleApiAddData?.results?.[0]?.address_components?.filter(
                  (part) => part && !part?.types?.includes("plus_code"),
                )[0]?.long_name
              }
            </Typography>
            <Typography
              fontSize={{ xs: 14, sm: 16 }}
              color={"rgba(63, 63, 63, 0.8)"}>
              {googleApiAddData?.results?.[0]?.formatted_address
                ?.split(",")
                .map((part) => part.trim())
                .filter((part) => part && !part.includes("+"))
                .join(", ")}
            </Typography>
          </Box>
        )}
      </Stack>
      {infoTile && <InfoTile text="Please ensure that the address entered is correct" />}
      </Stack>

    </Stack>
  );
}

const AddressFields = ({
  handleOnSubmit,
  fieldLabels,
  resetStateOnSubmit = false,
}) => {
  const t = useTranslations("WorkDetails");

  const {
    googleApiAddData,
    googleApiFallback,
    city,
    state,
    country,
    pin,
    latitude,
    longitude,
    line5,
    line2,
    line3,
    landmark,
    mapplsData,
    errorMessage: addresserror,
    start,
  } = useStore(
    StoreKeys.line2,
    StoreKeys.line3,
    StoreKeys.line5,
    StoreKeys.landmark,
    StoreKeys.city,
    StoreKeys.state,
    StoreKeys.country,
    StoreKeys.pin,
    StoreKeys.errorMessage,
    StoreKeys.longitude,
    StoreKeys.latitude,
    StoreKeys.googleApiAddData,
    StoreKeys.mapplsData,
    StoreKeys.googleApiFallback,
    StoreKeys.start,
  );
  const { dispatchStore } = useDispatchStore();

  const addressSchema = createAddressSchema(fieldLabels);

  const initialValues = useMemo(
    () => ({
      line5,
      line2,
      line3,
      landmark,
      latitude,
      longitude,
      pin,
      city,
      state,
      country,
    }),
    [
      line5,
      line2,
      line3,
      landmark,
      latitude,
      longitude,
      pin,
      city,
      state,
      country,
    ],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: addressSchema,
    onSubmit: async (values) => {
      const addressObject = {
        line1: values?.line1?.trim(),
        line2: values?.line2?.trim(),
        line3: values?.line3?.trim(),
        line4: values?.line4?.trim(),
        line5: values?.line5?.trim(),
        landmark: values?.landmark?.trim(),
        city: values?.city?.trim(),
        country: values?.country?.trim(),
        state: values?.state?.trim(),
        pin: values?.pin?.trim(),
        latitude,
        longitude,
        googleApiAddData: googleApiAddData,
        mapplsData: mapplsData,
      };
      if (typeof googleApiFallback === "boolean") {
        addressObject.isMapMyIndiaResult =
          !googleApiFallback && Boolean(mapplsData);
      }

      if (mapplsData && !googleApiFallback) {
        addressObject.line4 = mapplsData?.placeAddress;
      } else if (
        googleApiAddData &&
        googleApiAddData?.results?.[0]?.formatted_address
      ) {
        const components =
          googleApiAddData?.results?.[0]?.formatted_address || [];
        const mailingAddress = components
          ?.split(",")
          .map((part) => part.trim())
          .filter((part) => part && !part.includes("+"))
          .join(", ");

        addressObject.line4 = mailingAddress;
      }
      dispatchStore(values);

      try {
        await handleOnSubmit(addressObject);
        // reseting store
        resetStateOnSubmit && dispatchStore(initialState);
      } catch (error) {
        console.log("error in address module", error);
      }
    },
  });

  useEffect(() => {
    if (["line2", "line3", "landmark"]?.some((i) => formik.errors?.[i])) {
      formik.setFieldValue("line4", "");
    }
  }, [formik.errors]);

  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
    }
  };

  return (
    <Grid container gap={1}>
      <Grid item xs={12}>
        <Stack
          direction={"row"}
          alignItems="center"
          justifyContent={"space-between"}
          mb={1}>
          <Typography
            fontSize={{ xs: 18, md: 24 }}
            fontWeight={700}
            color={WEB_COLORS.primary}>
            Enter Detailed Address
          </Typography>
          <button
            style={{
              padding: "6px 18px",
              border: `1px solid ${WEB_COLORS.primary}`,
              color: WEB_COLORS.primary,
              background: "#fff",
              borderRadius: 4,
              cursor: "pointer",
              width: "fit-content",
            }}
            onClick={() => {
              dispatchStore({ ...initialState, googleApiFallback, stage: start, start });
            }}>
            Edit
          </button>
        </Stack>
        <DisplayMapAddress infoTile />
        {!addresserror ? (
          <form onSubmit={formik.handleSubmit}>
            <Grid container item gap={2} my={2}>
              <Grid item xs={12}>
                <TextFieldComponent
                  countRequired
                  allowCapitalization={true}
                  required
                  label={fieldLabels.line2}
                  name="line2"
                  placeholder={"Enter " + fieldLabels.line2}
                  maxLength={120}
                  onBlur={formik.handleBlur}
                  value={formik.values.line2}
                  error={Boolean(formik.touched.line2 && formik.errors.line2)}
                  onChange={(e) =>
                    handleFieldChange(e, FREE_WORK_ADDRESS_REGEX)
                  }
                  errorMessage={formik.touched.line2 && formik.errors.line2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldComponent
                  countRequired
                  allowCapitalization={true}
                  required
                  label={fieldLabels.line3}
                  name="line3"
                  placeholder={"Enter " + fieldLabels.line3}
                  maxLength={120}
                  onBlur={formik.handleBlur}
                  value={formik.values.line3}
                  error={Boolean(formik.touched.line3 && formik.errors.line3)}
                  onChange={(e) =>
                    handleFieldChange(e, FREE_WORK_ADDRESS_REGEX)
                  }
                  errorMessage={formik.touched.line3 && formik.errors.line3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldComponent
                  countRequired
                  maxLength={100}
                  required
                  allowCapitalization={true}
                  label={t("workLandmark")}
                  name="landmark"
                  placeholder={t("landmarkPlaceholder")}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    handleFieldChange(e, FREE_WORK_ADDRESS_REGEX);
                  }}
                  error={Boolean(
                    formik.touched.landmark && formik.errors.landmark,
                  )}
                  errorMessage={
                    formik.touched.landmark && formik.errors.landmark
                  }
                  value={formik.values.landmark}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldComponent
                  countRequired
                  maxLength={120}
                  name="line5"
                  label="Directions to Reach (Recommended)"
                  allowCapitalization={true}
                  value={formik?.values?.line5}
                  onChange={(e) => {
                    handleFieldChange(e, FREE_MAILING_ADDRESS_REGEX);
                  }}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.line5 && formik.errors.line5)}
                  errorMessage={formik.errors.line5}
                />
                <InfoTile text="Helps during physical verification" />
              </Grid>
              <Grid item xs={12} display={"none"} visibility={"hidden"}>
                <TextFieldComponent
                  maxLength={120}
                  shrink={true}
                  allowCapitalization={true}
                  disabled
                  label={t("city")}
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  error={Boolean(formik.touched.city && formik.errors.city)}
                  errorMessage={formik.errors.city}
                />
              </Grid>
              <Grid item xs={12} display={"none"} visibility={"hidden"}>
                <TextFieldComponent
                  shrink={true}
                  maxLength={120}
                  allowCapitalization={true}
                  disabled
                  label={t("state")}
                  name="state"
                  onChange={formik.handleChange}
                  value={formik.values.state}
                  error={Boolean(formik.touched.state && formik.errors.state)}
                  errorMessage={formik.errors.state}
                />
              </Grid>
              <Grid item xs={12} display={"none"} visibility={"hidden"}>
                <TextFieldComponent
                  maxLength={120}
                  shrink={true}
                  allowCapitalization={true}
                  disabled
                  label={t("pincode")}
                  name="pin"
                  onChange={formik.handleChange}
                  value={formik.values.pin}
                />
              </Grid>
              <Grid item xs={12} gap={1}>
                <InfoTile text="Incorrect information will lead to immediate rejection" />
                <InfoTile text="Physical verification over this address might be conducted" />
              </Grid>

              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  width: "100%",
                  zIndex: 10,
                  background: "#fff",
                  p: { xs: 0.5, md: 1 },
                }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    mt: { xs: 1, md: 2 },
                    fontSize: { xs: 14, md: 16 },
                    textTransform: "capitalize",
                    py: { xs: 1, md: 1.5 },
                  }}
                  type="submit">
                  Confirm Address
                </Button>
              </Box>
            </Grid>
          </form>
        ) : (
          <Stack
            width={"100%"}
            justifyContent={"center"}
            alignItems={"center"}
            py={{ xs: 2, md: 4 }}>
            <Typography
              color="error"
              p={2}
              fontSize={{ xs: "14px", md: "16px" }}
              textAlign="center">
              {addresserror}
            </Typography>
            <Box
              sx={{
                position: "relative",
                width: { xs: "80%", md: "auto" },
                maxWidth: { xs: "300px", md: "none" },
              }}>
              <Image
                src={IMAGES.addressMapPin}
                alt="Address Map Pin"
                style={{
                  width: "100%",
                  height: "auto",
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() =>
                dispatchStore({ ...initialState, googleApiFallback, stage: start, start })
              }
              sx={{
                mt: 3,
                fontSize: { xs: 14, md: 16 },
                textTransform: "capitalize",
                borderRadius: "8px",
                py: 1,
                flexGrow: 1,
                maxWidth: "150px",
              }}>
              Try Again
            </Button>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
};

function AddressAutosuggestion({
  currentLocation = true,
  fieldName,
  nextStage,
}) {
  const { latitude, longitude, googleApiFallback, googleSearched } = useStore(
    StoreKeys.latitude,
    StoreKeys.longitude,
    StoreKeys.googleApiFallback,
    StoreKeys.googleSearched,
  );

  const { dispatchStore } = useDispatchStore();

  const skipGoogleApi = !(googleApiFallback && googleSearched);
  const { refetch } = useGoogleApiHook({
    latitude,
    longitude,
    skip: skipGoogleApi,
    onSuccess: () => dispatchStore({ stage: nextStage || Stages.map }),
  });

  const handleCoordinates = useCallback(
    ({ lat, long, googleApiAddData: apiData }) => {
      const payload = {};
      if (lat) payload.latitude = lat;
      if (long) payload.longitude = long;
      if (apiData)
        payload.googleApiAddData = {
          results: apiData,
          status: "ok",
        };
      dispatchStore(payload);
    },
    [dispatchStore],
  );

  const handleMapplsCoordinates = useCallback(
    (data) => {
      const { latitude, longitude } = data || {};
      const payload = {};
      if (latitude) payload.latitude = latitude;
      if (longitude) payload.longitude = longitude;
      const addressError = getAddressError(data);
      if (addressError) {
        console.error(addressError);
        dispatchStore({
          errorMessage: addressError,
          stage: Stages.addressFields,
        });
        return;
      }

      payload.city = data?.city;
      payload.state = data?.state;
      payload.country = data?.country;
      payload.pin = data?.pin;
      payload.mapplsData = data;
      payload.stage = nextStage || Stages.map;
      dispatchStore(payload);
    },
    [dispatchStore],
  );

  return (
    <Stack position={"relative"}>
      <Typography
        fontSize={{ xs: 18, sm: 24 }}
        fontWeight={700}
        color={WEB_COLORS.primary}>
        Select Your Address
      </Typography>
      {!googleApiFallback ? (
        <MapplsAutosuggestion
          label={"Search your building, street, area or pincode"}
          onSelect={handleMapplsCoordinates}
          onError={(error) => {
            dispatchStore({ googleApiFallback: true });
          }}
        />
      ) : (
        <MapsAutocomplete
          keyName={fieldName}
          label={"Search your building, street, area or pincode"}
          handleOnSelect={(res) => handleCoordinates(res)}
        />
      )}
      {currentLocation && <CurrentLocation nextStage={nextStage} />}
    </Stack>
  );
}

function Content({ keyName, component }) {
  const { stage } = useStore(StoreKeys.stage);
  return keyName === stage ? component : undefined;
}

function Container(props) {
  const { dispatchStore } = useDispatchStore();
  useEffect(() => {
    if (props?.start) {
      dispatchStore({
        stage: props?.start,
      });
    }
    if (props?.start) {
      dispatchStore({
        start: props?.start,
      });
    }
  }, []);

  return (
    <Grid container gap={1}>
      <Grid item xs={12}>
        {props?.children}
      </Grid>
    </Grid>
  );
}

AddressModule.Container = Container;
AddressModule.Content = Content;
AddressModule.CurrentLocation = CurrentLocation;
AddressModule.Map = Map;
AddressModule.DisplayMapAddress = DisplayMapAddress;
AddressModule.AddressFields = AddressFields;
AddressModule.AddressAutosuggestion = AddressAutosuggestion;


export const initialAddressState = initialState;
export const useDispatchAddressStore = useDispatchStore;

/**
 * Exposing some methods to manipulate the address store outside the module, like resetting the store for scenarios like to reset address after submission. Instead of directly exposing store, it is advised to expose the required methods only.
 * 
 *  NOTE: use With Caution and only when necessary! use when you want to reset the address store outside the address Module (Store provider where conventional useDispatch is not accessible) -- NOT advisable though, Use in rarest of the cases.
 */ 
export const AddressStoreMethods = {
  resetAddressStore: () => addressStore.dispatch(storeSlice.actions.resetStore()),
}

